import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  CardBody,
  Progress,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { getAllSpecies } from 'common/src/api/species';
import {
  commitIndustryVolumeSeason,
  finishIndustryVolumeUpload,
  stageIndustryVolumeRows,
  startIndustryVolumeUpload,
  type IndustryVolumeCommitResult,
} from 'common/src/api/industryVolume';
import type { Species } from 'common/src/utils/types';
import { Alert, LoadingState } from '../ui';
import { FilePreview } from './file-preview';
import {
  chunk,
  invalidRows,
  type ParsedVolumeSheet,
} from '../../utils/industry-volume/parse-volume-sheet';

// Filas por llamada al subir: ~2 MB de JSON.
const BATCH_SIZE = 5000;

const FIELD_LABELS: Record<string, string> = {
  exporter: 'EXPORTADOR',
  kilos: 'KG.',
  season: 'TEMPORADA',
  etd: 'FECHA',
  variety: 'VARIEDAD',
};

const number = (value: number) => Math.round(value).toLocaleString('es-CL');
const millionKilos = (kilos: number) =>
  (kilos / 1e6).toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

type Step =
  | { kind: 'idle' }
  | { kind: 'reading' }
  | { kind: 'ready' }
  | { kind: 'uploading'; sent: number; total: number }
  | { kind: 'committing'; season: string; done: number; total: number }
  | { kind: 'done'; result: IndustryVolumeCommitResult }
  | { kind: 'error'; message: string };

// La base dice "CEREZAS"; la especie se elige igual, esto solo la preselecciona.
const guessSpeciesId = (fileSpecies: string[], species: Species[]) => {
  if (fileSpecies.some((name) => name.toUpperCase().startsWith('CEREZ'))) {
    return species.find((s) => s.excel_name?.toUpperCase() === 'CHERRY')?.id ?? null;
  }
  return null;
};

const readInWorker = (file: File) =>
  new Promise<ParsedVolumeSheet>((resolve, reject) => {
    const worker = new Worker(
      new URL('../../workers/volume-excel.worker.ts', import.meta.url),
      { type: 'module' }
    );
    worker.onmessage = (event) => {
      worker.terminate();
      if (event.data.ok) resolve(event.data.result);
      else reject(new Error(event.data.error));
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || 'No se pudo leer el archivo'));
    };
    file.arrayBuffer().then(
      (buffer) => worker.postMessage(buffer, [buffer]),
      reject
    );
  });

/**
 * Carga de la base de volumen de industria (una fila por embarque, todas las
 * exportadoras). Se lee en un worker, se manda por partes y después se carga
 * cada temporada del archivo (reemplazándola o agregándola). Si una temporada
 * falla, las ya cargadas quedan y esa queda como estaba.
 */
export default function VolumeUploadPanel() {
  const [species, setSpecies] = useState<Species[]>([]);
  const [speciesId, setSpeciesId] = useState<number | null>(null);
  const [mode, setMode] = useState<'replace' | 'append'>('replace');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedVolumeSheet | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'idle' });

  useEffect(() => {
    getAllSpecies().then(setSpecies).catch(() => setSpecies([]));
  }, []);

  const reset = () => {
    setFile(null);
    setParsed(null);
    setStep({ kind: 'idle' });
  };

  const onDrop = useCallback(
    async (files: File[]) => {
      const selected = files[0];
      if (!selected) return;
      setFile(selected);
      setParsed(null);
      setStep({ kind: 'reading' });
      try {
        const result = await readInWorker(selected);
        setParsed(result);
        setSpeciesId((current) => current ?? guessSpeciesId(result.species, species));
        setStep({ kind: 'ready' });
      } catch (error) {
        setStep({
          kind: 'error',
          message: `No se pudo leer el archivo: ${(error as Error).message}`,
        });
      }
    },
    [species]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
  });

  const invalid = parsed ? invalidRows(parsed.rows) : [];
  const canUpload =
    step.kind === 'ready' &&
    !!parsed &&
    !!speciesId &&
    parsed.rows.length > 0 &&
    parsed.missingFields.length === 0 &&
    invalid.length === 0;
  const busy = step.kind === 'uploading' || step.kind === 'committing';

  const upload = async () => {
    if (!parsed || !speciesId || !file) return;
    const batches = chunk(parsed.rows, BATCH_SIZE);
    setStep({ kind: 'uploading', sent: 0, total: parsed.rows.length });
    try {
      const uploadId = await startIndustryVolumeUpload(speciesId, file.name, mode);
      let sent = 0;
      for (const batch of batches) {
        sent += await stageIndustryVolumeRows(uploadId, batch);
        setStep({ kind: 'uploading', sent, total: parsed.rows.length });
      }
      // Una temporada por llamada: cada una se reemplaza en su transacción.
      const seasons = parsed.seasons.map((s) => s.season);
      for (const [index, season] of seasons.entries()) {
        setStep({ kind: 'committing', season, done: index, total: seasons.length });
        await commitIndustryVolumeSeason(uploadId, season);
      }
      const result = await finishIndustryVolumeUpload(uploadId);
      setStep({ kind: 'done', result });
    } catch (error) {
      setStep({ kind: 'error', message: (error as Error).message });
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex max-w-xl flex-wrap gap-4'>
        <Select
          label='Especie'
          selectedKeys={speciesId ? [String(speciesId)] : []}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0];
            setSpeciesId(key ? Number(key) : null);
          }}
          isDisabled={busy}
          isRequired
          className='min-w-[200px] flex-1'
        >
          {species.map((s) => (
            <SelectItem key={String(s.id)}>{s.name}</SelectItem>
          ))}
        </Select>
        <Select
          label='Modo de carga'
          selectedKeys={[mode]}
          disallowEmptySelection
          onSelectionChange={(keys) =>
            setMode((Array.from(keys)[0] as 'replace' | 'append') || 'replace')
          }
          isDisabled={busy}
          className='min-w-[220px] flex-1'
          description={
            mode === 'append'
              ? 'Solo inserta; falla si alguna temporada del archivo ya tiene datos.'
              : 'Borra y vuelve a cargar las temporadas que trae el archivo.'
          }
        >
          <SelectItem key='replace'>Reemplazar las temporadas del archivo</SelectItem>
          <SelectItem key='append'>Solo agregar temporadas nuevas</SelectItem>
        </Select>
      </div>

      {!file ? (
        <div
          {...getRootProps()}
          className={`flex h-64 w-full max-w-2xl cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
            isDragActive
              ? 'border-primary bg-primary-50'
              : 'border-default-300 hover:border-default-400'
          }`}
        >
          <input {...getInputProps()} />
          <Icon icon='tabler:upload' width={48} className='mb-4 text-default-400' />
          <p className='mb-2 text-lg'>
            {isDragActive
              ? 'Suelta el archivo aquí'
              : 'Arrastra la base de volumen (Excel) aquí'}
          </p>
          <p className='text-sm text-default-500'>
            Una fila por embarque: exportador, variedad, kg, destino, nave,
            fecha y temporada.
          </p>
        </div>
      ) : (
        <FilePreview file={file} />
      )}

      {step.kind === 'reading' && (
        <LoadingState label='Leyendo el archivo… puede tardar unos segundos.' />
      )}

      {step.kind === 'error' && (
        <Alert variant='danger' showIcon>
          {step.message}
        </Alert>
      )}

      {parsed && step.kind !== 'reading' && (
        <>
          {parsed.missingFields.length > 0 && (
            <Alert variant='danger' showIcon>
              Faltan columnas en el archivo:{' '}
              {parsed.missingFields.map((f) => FIELD_LABELS[f] ?? f).join(', ')}.
            </Alert>
          )}
          {invalid.length > 0 && (
            <Alert variant='danger' showIcon>
              {number(invalid.length)} filas sin temporada, kg o exportador
              (filas {invalid.slice(0, 10).map((r) => r.row_number).join(', ')}
              {invalid.length > 10 ? '…' : ''}). Corrígelas y vuelve a subir el
              archivo.
            </Alert>
          )}
          {parsed.species.length > 1 && (
            <Alert variant='warning' showIcon>
              El archivo trae más de una especie ({parsed.species.join(', ')}).
              Todas las filas se van a cargar como la especie elegida.
            </Alert>
          )}

          <Card shadow='sm'>
            <CardBody className='gap-3 p-6'>
              <h3 className='text-lg font-semibold'>
                {number(parsed.rows.length)} filas en{' '}
                {parsed.seasons.length} temporadas
              </h3>
              <Table aria-label='Temporadas del archivo' removeWrapper isStriped isCompact>
                <TableHeader>
                  <TableColumn>TEMPORADA</TableColumn>
                  <TableColumn className='text-right'>FILAS</TableColumn>
                  <TableColumn className='text-right'>M KG</TableColumn>
                  <TableColumn className='text-right'>EXPORTADORAS</TableColumn>
                  <TableColumn>PRIMER ZARPE</TableColumn>
                  <TableColumn>ÚLTIMO ZARPE</TableColumn>
                </TableHeader>
                <TableBody>
                  {parsed.seasons.map((s) => (
                    <TableRow key={s.season}>
                      <TableCell>{s.season}</TableCell>
                      <TableCell className='text-right'>{number(s.rows)}</TableCell>
                      <TableCell className='text-right'>{millionKilos(s.kilos)}</TableCell>
                      <TableCell className='text-right'>{number(s.exporters)}</TableCell>
                      <TableCell className='whitespace-nowrap'>{s.firstEtd ?? '—'}</TableCell>
                      <TableCell className='whitespace-nowrap'>{s.lastEtd ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {mode === 'replace' && (
                <p className='text-sm text-default-500'>
                  Se van a reemplazar estas temporadas para la especie elegida.
                  Las demás temporadas no se tocan.
                </p>
              )}
            </CardBody>
          </Card>

          {step.kind === 'uploading' && (
            <Progress
              aria-label='Subiendo filas'
              label={`Subiendo filas… ${number(step.sent)} de ${number(step.total)}`}
              value={step.total ? (step.sent / step.total) * 100 : 0}
              showValueLabel
            />
          )}
          {step.kind === 'committing' && (
            <Progress
              aria-label='Cargando temporadas'
              label={`Cargando temporada ${step.season} (${step.done + 1} de ${step.total})…`}
              value={(step.done / step.total) * 100}
              showValueLabel
            />
          )}

          {step.kind === 'done' && (
            <Alert variant='success' showIcon>
              <div className='flex flex-col gap-1'>
                <span className='font-medium'>
                  Se cargaron {number(step.result.inserted_rows)} filas (
                  {step.result.seasons.join(', ')})
                  {step.result.mode === 'replace'
                    ? `, reemplazando ${number(step.result.deleted_rows)} anteriores.`
                    : '.'}
                </span>
                <span>
                  Clientes vinculados: {number(step.result.mappings.clients_linked)}.
                  Puertos sin calzar: {number(step.result.mappings.ports_unmatched)} (
                  {millionKilos(step.result.mappings.ports_unmatched_kilos)} M kg).
                  Naves sin calzar: {number(step.result.mappings.ships_unmatched)} (
                  {millionKilos(step.result.mappings.ships_unmatched_kilos)} M kg).
                </span>
              </div>
            </Alert>
          )}

          <div className='flex justify-end gap-4'>
            <Button
              variant='light'
              startContent={<Icon icon='tabler:x' />}
              onPress={reset}
              isDisabled={busy}
            >
              {step.kind === 'done' ? 'Subir otro archivo' : 'Cancelar'}
            </Button>
            {step.kind !== 'done' && (
              <Button
                color='primary'
                startContent={<Icon icon='tabler:upload' />}
                onPress={upload}
                isDisabled={!canUpload}
                isLoading={busy}
              >
                Subir a la base de datos
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
